import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Column,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormField,
  InputTextField,
  LoadingButton,
  Typography,
} from '@tempmee/react';
import { applyPromoCodeErrorMessage, useApplyOfficePromoCode } from '../../../../domain/Campaigns';

/**
 * Applies a promo code to the office on its behalf (GOTU-9821). One field, the
 * code; campaign-service trims it and resolves it, so nothing is normalised here.
 *
 * Mounted only while open, so every opening starts from an empty field and no
 * leftover error.
 *
 * A refusal — unknown code, ended campaign, office outside the targeting, a
 * code the office already holds, a missing permission — is shown as plain copy
 * keyed on the service's `reason`, never its engineer-facing `message` (see
 * `promoCodeErrors.js`). The already-held 409 is a refusal on purpose, so the
 * admin sees that nothing changed rather than a dialog that just closes.
 *
 * Apply is disabled on a blank code: the service answers an empty one with a
 * validator string (`binding:"required"`) rather than a readable message.
 */
export const ApplyPromoCodeDialog = ({ officeId, onClose }) => {
  const [code, setCode] = useState('');
  const [submitError, setSubmitError] = useState(null);

  const applyCode = useApplyOfficePromoCode(officeId);

  const handleSubmit = () => {
    setSubmitError(null);
    // Per-call callbacks, so a request that settles after the dialog was
    // closed does not close (or error) whatever is open by then.
    applyCode.mutate(code, {
      onSuccess: onClose,
      onError: (error) => setSubmitError(applyPromoCodeErrorMessage(error)),
    });
  };

  return (
    <Dialog open onClose={onClose} closeOnOverlayClick={false}>
      <DialogTitle>Apply promo code</DialogTitle>

      <DialogContent>
        <Column gap={4}>
          <FormField label="Promo code" htmlFor="apply-promo-code" required>
            <InputTextField
              id="apply-promo-code"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              isError={Boolean(submitError)}
              aria-describedby={submitError ? 'apply-promo-code-error' : undefined}
            />
          </FormField>

          {submitError ? (
            <Typography
              id="apply-promo-code-error"
              role="alert"
              variant="body2"
              className="text-error"
            >
              {submitError}
            </Typography>
          ) : null}
        </Column>
      </DialogContent>

      <DialogActions>
        <Button variant="outlined" color="secondary" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          loading={applyCode.isPending}
          disabled={!code.trim()}
          onClick={handleSubmit}
        >
          Apply
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

ApplyPromoCodeDialog.propTypes = {
  officeId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ApplyPromoCodeDialog;
